import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { IconFieldModule } from 'primeng/iconfield';
import { InputGroup } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputIconModule } from 'primeng/inputicon';
import { InputMaskModule } from 'primeng/inputmask';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { MenuModule } from 'primeng/menu';
import { Action } from '../../../models/core/action';
import { MenuComponent } from '../panels/menu.component';
import { BaseControlComponent } from './base-control.component';
import { FormFieldComponent } from './form-field.component';
@Component({
  selector: 'app-ssn',
  imports: [
    ReactiveFormsModule,
    CommonModule,
    InputTextModule,
    FormFieldComponent,
    IconFieldModule,
    InputIconModule,
    InputNumberModule,
    InputMaskModule,
    MenuModule,
    InputGroupAddonModule,
    InputGroup,
    ButtonModule,
    MenuComponent,
  ],

  template: `
    <app-form-field
      [control]="control"
      [id]="id"
      [label]="type === 'Full' ? label : label + ' (Last 4)'"
      [helpText]="helpText"
      [showHelpText]="showHelpText"
    >
      <div class="tw-flex tw-items-center">
        <p-inputgroup>
          @if (type === 'Full') {
            <p-inputmask
              [formControl]="control"
              [id]="id"
              class="tw-w-full"
              styleClass="app-input-border"
              [style]="{
                width: '100%',
                height: '40px',
                'border-radius': '6px 0 0 6px',
              }"
              mask="999-99-9999"
              [unmask]="true"
              [class]="{
                'ng-invalid': touchedInvalid,
                'ng-dirty': touchedInvalid,
              }"
            />
          } @else {
            <p-inputmask
              [formControl]="control"
              [id]="id"
              class="tw-w-full"
              styleClass="app-input-border"
              [style]="{
                width: '100%',
                height: '40px',
                'border-radius': '6px 0 0 6px',
              }"
              mask="9999"
              [unmask]="true"
              [class]="{
                'ng-invalid': touchedInvalid,
                'ng-dirty': touchedInvalid,
              }"
            />
          }

          <p-inputgroup-addon
            styleClass="app-input-border"
            [style]="{
              width: '35px',
              height: '40px',
              padding: '0',
            }"
          >
            <button
              type="button"
              tabindex="0"
              class="p-button p-button-icon-only p-button-secondary p-button-text menu-button focus-ring-icon-button"
              style="width: 35px; height: 38px; min-width: 35px;"
              aria-label="SSN format options"
              [attr.aria-expanded]="isMenuExpanded"
              aria-haspopup="menu"
              (click)="menu.toggle($event)"
              (keydown.enter)="menu.toggle($event)"
              (keydown.space)="menu.toggle($event)"
            >
              <span
                class="p-button-icon fa-ellipsis-vertical fa-light"
                aria-hidden="true"
              ></span>
            </button>
          </p-inputgroup-addon>
        </p-inputgroup>
        <app-menu
          [items]="actions"
          #menu
          appendTo="body"
          (click)="setType($event)"
          (visibilityChange)="isMenuExpanded = $event"
        />
      </div>
    </app-form-field>
  `,
})
export class SsnComponent extends BaseControlComponent implements OnInit {
  @Input()
  type: SsnType = 'Full';

  @Input()
  set allowFull(value: boolean) {
    this._allowFull = value !== false;
    this.updateActions();
  }
  get allowFull(): boolean {
    return this._allowFull;
  }

  @Input()
  set allowLastFour(value: boolean) {
    this._allowLastFour = value !== false;
    this.updateActions();
  }
  get allowLastFour(): boolean {
    return this._allowLastFour;
  }

  /** Tracks menu expanded state for accessibility */
  isMenuExpanded = false;

  actions: Action[] = [];

  private _allowFull = true;
  private _allowLastFour = true;

  protected setType(action: Action) {
    this.type = action.key === 'full' ? 'Full' : 'LastFour';
    this.ensureAllowedType();
  }

  ngOnInit(): void {
    this.updateActions();
    this.dispose.subs = this.control.valueChanges.subscribe((x) => {
      const inputElementToFill: HTMLInputElement = document.getElementById(
        this.id,
      )?.firstChild as HTMLInputElement;
      if (x) {
        inputElementToFill?.classList.add('p-filled');
      } else {
        inputElementToFill?.classList.remove('p-filled');
      }
      inputElementToFill?.setAttribute('label', this.label);
      inputElementToFill?.setAttribute('name', this.label);
      inputElementToFill?.setAttribute('aria-label', this.label);
    });
  }

  private updateActions(): void {
    const nextActions: Action[] = [];

    if (this.allowFull) {
      nextActions.push({ label: 'Full SSN', key: 'full' });
    }

    if (this.allowLastFour) {
      nextActions.push({ label: 'Last Four', key: 'last-four' });
    }

    if (nextActions.length === 0) {
      nextActions.push({ label: 'Last Four', key: 'last-four' });
      this.type = 'LastFour';
    }

    this.actions = nextActions;
    this.ensureAllowedType();
  }

  private ensureAllowedType(): void {
    if (this.type === 'Full' && !this.allowFull && this.allowLastFour) {
      this.type = 'LastFour';
    }

    if (this.type === 'LastFour' && !this.allowLastFour && this.allowFull) {
      this.type = 'Full';
    }
  }
}

export type SsnType = 'Full' | 'LastFour';
